import { useMemo, useState } from 'react';
import {
  createSchedulerDataHook,
  createSchedulerMutationActionsHook,
  createWorkdaySyncHook,
  createUseShiftChangeHook,
  createUseUnblockShiftHook,
  createSchedulerShiftHighlightHook,
} from '@lp/features/Scheduler/factories';
import { getOMSResourceShiftTotalsMap } from '@lp/features/Scheduler/SchedulerOMS/utils';
import { transformResourcesViolations } from '@lp/features/Scheduler/validation.utils';
import { useOMSSchedulerHighlightStore } from '@lp/core/store/scheduler';
import { transformOMSCountryViolations } from '@lp/features/Scheduler/SchedulerOMS/validation.utils';
import { OMSSchedulerViewType } from '@lp/features/Scheduler/SchedulerOMS/types';
import {
  useOMSMonthScheduleQuery,
  useOMSPlanningProcessQuery,
  useOMSScheduleShiftsQuery,
  useOMSCreatePlanningMutation,
  useOMSRegeneratePlanningMutation,
  useOMSCancelPlanningMutation,
  useOMSPublishPlanningMutation,
  useOMSCancelGenerationPlanningMutation,
  useOMSScheduleSyncWorkdayMutation,
  useOMSHrSystemConflictsQuery,
  useOMSHrSystemConflictResolveMutation,
  useOMSScheduleSetShiftTypeOptimisticMutation,
  useOMSScheduleResetShiftTypeOptimisticMutation,
  useOMSValidationQuery,
  useOMSSetFixationTypeOptimisticMutation,
} from '@/queries/resource/schedule';
import { OMSResourcesWithShifts, PlanningProcess, PlanningProcessStatus } from '@/api/apis/resources/schedule';

export const useOMSScheduleViewMode = (initialViewMode = OMSSchedulerViewType.Scheduler) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const isPersonalView = viewMode === OMSSchedulerViewType.Personal;
  const isGridView = isPersonalView || viewMode === OMSSchedulerViewType.Duty;

  return { viewMode, isPersonalView, isGridView, setViewMode };
};

export const useOMSSchedulerData = createSchedulerDataHook({
  useMonthScheduleQuery: useOMSMonthScheduleQuery,
  usePlanningProcessQuery: useOMSPlanningProcessQuery,
  useScheduleShiftsQuery: useOMSScheduleShiftsQuery,
});

export const useOMSSchedulerMutationActions = createSchedulerMutationActionsHook({
  useCreatePlanningMutation: useOMSCreatePlanningMutation,
  useRegeneratePlanningMutation: useOMSRegeneratePlanningMutation,
  useCancelPlanningMutation: useOMSCancelPlanningMutation,
  usePublishPlanningMutation: useOMSPublishPlanningMutation,
  useCancelGenerationPlanningMutation: useOMSCancelGenerationPlanningMutation,
});

export const useOMSWorkdaySync = createWorkdaySyncHook({
  useWorkdaySyncMutation: useOMSScheduleSyncWorkdayMutation,
  useGetWorkdayConflicts: useOMSHrSystemConflictsQuery,
  useHrSystemConflictResolveMutation: useOMSHrSystemConflictResolveMutation,
});

export const useOMSSchedulerServices = (selectedDay: Date, shiftsData?: OMSResourcesWithShifts) => {
  const userList = useMemo(() => {
    if (!shiftsData) return null;
    return Object.values(shiftsData).map((s) => s.resource);
  }, [shiftsData]);

  const resourceShiftTotalsMap = useMemo(() => {
    if (!shiftsData) return undefined;
    return getOMSResourceShiftTotalsMap(selectedDay, shiftsData);
  }, [selectedDay, shiftsData]);

  return {
    userList,
    resourceShiftTotalsMap,
  };
};

export const useOMSShiftChange = createUseShiftChangeHook({
  useScheduleSetShiftTypeOptimisticMutation: useOMSScheduleSetShiftTypeOptimisticMutation,
  useScheduleResetShiftTypeOptimisticMutation: useOMSScheduleResetShiftTypeOptimisticMutation,
});

export const useOMSUnblockShift = createUseUnblockShiftHook({
  useSetFixationTypeOptimisticMutation: useOMSSetFixationTypeOptimisticMutation,
});

export const useOMSScheduleValidation = (
  planningData?: PlanningProcess,
  shiftsData?: OMSResourcesWithShifts,
  enabled?: boolean,
) => {
  const { scheduleId, status } = planningData ?? {};
  const isEditing = status === PlanningProcessStatus.Editing;
  const canValidate = Boolean(enabled && scheduleId && isEditing);
  const { data, isFetching } = useOMSValidationQuery(scheduleId, canValidate);
  const fillingState = data?.scheduleSize.fillingState;

  const { byDayMap, dayMessagesList } = useMemo(() => {
    return transformOMSCountryViolations(data);
  }, [data]);

  const { byResourceMap, resourcesMessagesList } = useMemo(() => {
    return transformResourcesViolations(data?.resourceRulesViolations, shiftsData);
  }, [data?.resourceRulesViolations, shiftsData]);

  const count = useMemo(() => {
    const allMessages = [...resourcesMessagesList, ...dayMessagesList].flatMap((v) => v.messages);
    return allMessages.length;
  }, [resourcesMessagesList, dayMessagesList]);

  if (!enabled) return undefined;

  return {
    count,
    byDayMap,
    byResourceMap,
    resourcesMessagesList,
    dayMessagesList,
    isFetching,
    fillingState,
    publishReadiness: data?.publishReadiness,
  };
};

export const useOMSSchedulerShiftHighlight = createSchedulerShiftHighlightHook({
  useSetHighlightValue: useOMSSchedulerHighlightStore.use.setHighlightValue,
});
